/* eslint-disable no-underscore-dangle */

'use strict';

define('vb/private/stateManagement/router/urlPathAdapter',[
  'vb/private/stateManagement/router/urlAdapter',
  'vb/private/stateManagement/router/vbRouter',
  'vb/private/log',
], (UrlAdapter, VbRouter, Log) => {
  const log = Log.getLogger('/vb/stateManagement/router/urlPathAdapter');

  /**
   * @typedef {import('vb/private/stateManagement/router/stateChange').default} StateChange
   */

  /**
   * Url adapter used by the {@link VbRouter} to manage URL in the form of
   * <code class="prettyprint">/book/chapter2</code>.<br>The UrlPathAdapter is the default
   * adapter used by the {@link VbRouter|router} as it makes more human-readable URLs,
   * is user-friendly, and less likely to exceed the maximum character limit in the
   * browser URL.
   * <br>Since this adapter generates path URLs, it's advisable that your application
   * be able to restore the page should the user bookmark or reload the page.  For
   * instance, given the URL <code class="prettyprint">/book/chapter2</code>, your
   * application server ought to serve up content for "chapter2" if the user should
   * bookmark or reload the page.  If that's not possible, then consider using the
   * {@link VbRouter.UrlParamAdapter|urlParamAdapter}.
   * <br>There are two available URL adapters,
   * this one and the {@link UrlParamAdapter|urlParamAdapter}.<br>To change
   * the URL adapter, use the {@link VbRouter.defaults|urlAdapter} property.
   * @see VbRouter.UrlParamAdapter
   * @see VbRouter.defaults
   */
  class UrlPathAdapter extends UrlAdapter {
    constructor() {
      super();

      /**
       * Variable to store the base path. This is used to retrieve the portion of the path
       * representing the routers state.
       * @private
       * @type {string|null}
       */
      this._basePath = null;

      this.init(VbRouter.defaults.baseUrl);
    }

    /**
     * Initialize the adapter given the baseUrlProp.
     * For the urlPathAdapter, retrieve the potential file name to handle application with
     * index.html in their URL.
     * @param {!string} baseUrlProp the value of VbRouter.defaults.baseUrl
     */
    init(baseUrlProp) {
      // Use the browser parser to get the pathname. It works with absolute or relative URL.
      const parser = document.createElement('a');
      parser.href = baseUrlProp;

      let path = parser.pathname;
      path = path.replace(/^([^/])/, '/$1'); // Should always start with slash (for IE)

      // Normalize the base path. Always ends with a '/'
      if (path.slice(-1) !== '/') {
        path += '/';
      }

      this._basePath = path;
    }

    /**
     * To retrieve the portion of the path representing the router state,
     * remove the base portion of the path.
     * @returns {string}
     */
    getPath() {
      return window.location.pathname.replace(this._basePath || '', '');
    }

    /**
     * Construct an array of states where each item is an object made of a router and
     * the new state for it.
     * @param {Object} transition An object with properties describing the transition.
     * @return {!Array.<StateChange>}
     * @throws Error when parsing of router param fails.
     */
    parse(transition) {
      const path = this.getPath();
      const segments = VbRouter.getSegments(decodeURIComponent(path)).map(VbRouter.decodeSlash);

      log.finer('Parsing:', path);
      const changes = UrlAdapter.getChangesFromSegments(transition.rootRouter, segments);

      return changes;
    }

    /**
     * Given an ordered array of states, build the URL representing all
     * the states.
     * Never starts with a '/': "book"  "book/chapter2"
     * @param {!Array.<StateChange>} newStates
     * @return {!string} the URL representing the states
     */
    buildUrlFromStates(newStates) {
      let canDefault = false;
      let pathname = '';

      // Build the new URL by walking the array of states backward in order to eliminate
      // the default state from the URL. As soon as a value is not the default, stops the removal.
      for (let ns = newStates.pop(); ns; ns = newStates.pop()) {
        if (ns.value) {
          if (canDefault || (ns.value !== ns.router._defaultStateId)) {
            pathname = pathname ? `${ns.value}/${pathname}` : ns.value;
            canDefault = true;
          }
        }
      }

      return VbRouter.buildUrl({ pathname: this._basePath + pathname });
    }
  }

  return UrlPathAdapter;
});

